import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'

import DialogModal from '../../ui/DialogModal/DialogModal'
import Icon from '../../ui/Icon/Icon'
import ActionButton from '../../ui/ActionButton/ActionButton'
import ConsentModal from '../ConsentModal/ConsentModal'

import MasterCard from '../../../assets/img/card-linking/mastercard.svg'
import VisaCard from '../../../assets/img/card-linking/visacard.svg'
import CardsError from '../CardsError/CardsError'
import { setGAEvent } from '../../../services/analytics/google-analytics'
import useFeatureMaintenance, {
  FEATURES_LIST,
} from '../../../services/hooks/useFeatureMaintenance'
import FeatureMaintenanceModal from '../FeatureMaintenanceModal/FeatureMaintenanceModal'

const CardListWrapper = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`

const Description = styled.p`
  font-size: 0.875rem;
  letter-spacing: 0.1px;
  line-height: 1.3125rem;
  margin: 2rem 0;
  text-align: center;
`

const CardListItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
  border-radius: var(--border-radius-card);
  margin-bottom: 10px;
  padding: 14px;
  box-shadow: 0 3px 8px 0 rgba(48, 31, 83, 0.07);
`

const DeleteButton = styled.button`
  font-size: 1.2rem;
  background-color: transparent;
  color: var(--color-primary);
  cursor: pointer;
  border: 0;
`

const CardTypeImage = styled.img`
  width: 30px;
`
const CardNumber = styled.div`
  flex-grow: 1;
  padding-left: 15px;
`

const CardImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  margin-bottom: 40px;
`

const CardImage = styled.img`
  margin: 8px;
`

const BlockButton = styled(ActionButton)`
  width: 100%;
`

const StyledCardsError = styled(CardsError)`
  margin: 0 0 1rem 0;
`

const StyledConsentModal = styled(ConsentModal).attrs({ zIndex: '10021' })`
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
`

function CardList(props) {
  const {
    items,
    onDelete,
    onAddClick,
    loadingStatus,
    cardError,
    hasCardsErrorButton,
    hasCardError,
    registration,
  } = props
  const {
    loading,
    isFeatureAvailable,
    refreshFeatureStatus,
  } = useFeatureMaintenance({ feature: FEATURES_LIST.CARDS })
  const [showMaintenance, setShowMaintenance] = React.useState(false)
  const [dialogState, setDialogState] = React.useState({
    selectedCardItem: {},
    modalVisible: false,
  })
  const { selectedCardItem, modalVisible } = dialogState
  const setDeleteDialogState = state => {
    setDialogState({
      ...dialogState,
      ...state,
    })
  }

  const toggleMaintenanceModal = () => {
    setShowMaintenance(!showMaintenance)
  }
  const handleAddCardClick = () => {
    if (isFeatureAvailable()) {
      onAddClick()
    } else {
      toggleMaintenanceModal()
    }
  }

  React.useEffect(() => {
    refreshFeatureStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CardListWrapper>
      {items.map(item => (
        <CardListItem>
          <CardTypeImage
            src={item.scheme.toUpperCase() === 'VISA' ? VisaCard : MasterCard}
          />
          <CardNumber>{item.number}</CardNumber>
          <DeleteButton
            onClick={() => {
              setDeleteDialogState({
                selectedCardItem: item,
                modalVisible: true,
              })
            }}
          >
            <Icon name="x" />
          </DeleteButton>
        </CardListItem>
      ))}

      {!hasCardError && (
        <BlockButton
          variant="primary"
          onClick={handleAddCardClick}
          disabled={loadingStatus !== 1 || items.length >= 3 || loading}
          style={{
            filter: items.length >= 3 ? 'saturate(0)' : null,
          }}
        >
          Add Credit Card
        </BlockButton>
      )}

      <Description>
        Link up to three (3) credit cards to your Zing account and keep earning
        Zing points in Ayala Malls!
        <br />
        <br />
        Don't worry, we safekeep your card details using global security
        standards.
      </Description>
      {hasCardsErrorButton && (
        <StyledCardsError
          display={cardError.display}
          errorType={cardError.type}
          onRetry={cardError.onRetry}
        />
      )}
      <CardImageWrapper>
        <CardImage src={MasterCard} height={32} />
        <CardImage src={VisaCard} height={20} />
      </CardImageWrapper>
      <Description>
        By adding and linking your credit card, you agree to the program{' '}
        <StyledConsentModal
          onClick={() =>
            registration &&
            setGAEvent(
              'button_cta',
              'Registration - Card Linking',
              'Card Holder Consent & Transaction Monitoring',
            )
          }
        >
          Cardholder Consent & Transaction Monitoring
        </StyledConsentModal>
      </Description>

      <FeatureMaintenanceModal
        show={showMaintenance}
        onConfirmClick={toggleMaintenanceModal}
      />
      <DialogModal
        modalState={modalVisible}
        title="Delete Card"
        description={`Are you sure you want to delete this card? ${selectedCardItem.number}`}
        okText="Yes"
        onOk={() => {
          onDelete(selectedCardItem.id)
          setDeleteDialogState({
            modalVisible: false,
          })
        }}
        onCancel={() => setDeleteDialogState({ modalVisible: false })}
      />
    </CardListWrapper>
  )
}

CardList.defaultProps = {
  items: [],
  firstName: '',
  onDelete: () => {},
  onAddClick: () => {},
}

CardList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ),
  firstName: PropTypes.string,
  onDelete: PropTypes.func,
  onAddClick: PropTypes.func,
}

export default CardList
